/* GreenFox Quick View Modal */

class GfQuickView extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('.gf-quick-view-overlay');
    this.dialog = this.querySelector('.gf-quick-view__dialog');
    this.content = this.querySelector('.gf-quick-view__content');
    this.closeBtn = this.querySelector('.gf-quick-view__close');

    this._onCloseClick = () => this.close();
    this._onOverlayClick = (e) => {
      if (e.target === this.overlay) this.close();
    };
    this._onKeydown = (e) => {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (e.key === 'Tab') {
        this._handleTab(e);
      }
    };

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this._onCloseClick);
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', this._onOverlayClick);
    }
    document.addEventListener('keydown', this._onKeydown);

    this.isOpen = false;
    this.previousFocus = null;
  }

  disconnectedCallback() {
    if (this.closeBtn) {
      this.closeBtn.removeEventListener('click', this._onCloseClick);
    }
    if (this.overlay) {
      this.overlay.removeEventListener('click', this._onOverlayClick);
    }
    document.removeEventListener('keydown', this._onKeydown);
  }

  async open(productUrl, sectionId) {
    if (!this.overlay || !this.content) return;
    this._triggerSectionId = sectionId || 'gf-main-product';

    this.previousFocus = document.activeElement;
    this.isOpen = true;

    this.content.textContent = '';
    this.content.setAttribute('aria-busy', 'true');
    const loading = document.createElement('div');
    loading.className = 'gf-quick-view__loading';
    loading.setAttribute('role', 'status');
    loading.textContent = '...';
    this.content.appendChild(loading);

    this.overlay.classList.add('gf-quick-view-overlay--open');
    document.body.style.overflow = 'hidden';

    if (this.closeBtn) this.closeBtn.focus();

    try {
      const sectionId = this._triggerSectionId || 'gf-main-product';
      const separator = productUrl.includes('?') ? '&' : '?';
      const response = await fetch(`${productUrl}${separator}section_id=${sectionId}`);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      this.content.textContent = '';
      this.content.removeAttribute('aria-busy');

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const section = doc.querySelector('.gf-product');

      if (section) {
        this.content.appendChild(document.importNode(section, true));
        const title = section.querySelector('h1');
        if (title && this.dialog) {
          this.dialog.setAttribute('aria-label', title.textContent.trim());
        }
      }

      /* Import variant JSON scripts that live outside .gf-product */
      doc.querySelectorAll('script[type="application/json"][id^="gf-product-variants-"]').forEach((jsonScript) => {
        this.content.appendChild(document.importNode(jsonScript, true));
      });
    } catch (err) {
      this.content.textContent = '';
      const errorMsg = document.createElement('p');
      errorMsg.textContent = this.dataset.errorMessage || 'Could not load product.';
      errorMsg.style.textAlign = 'center';
      errorMsg.style.padding = '2rem';
      this.content.appendChild(errorMsg);
    }

    this._trapFocus();
  }

  close() {
    if (!this.overlay) return;

    this.isOpen = false;
    this.overlay.classList.remove('gf-quick-view-overlay--open');
    document.body.style.overflow = '';

    if (this.previousFocus) {
      this.previousFocus.focus();
    }
  }

  _trapFocus() {
    if (!this.dialog) return;
    const focusable = this._getFocusable();
    if (focusable.length > 0) {
      focusable[0].focus();
    } else if (this.closeBtn) {
      this.closeBtn.focus();
    }
  }

  _handleTab(e) {
    if (!this.dialog) return;
    const focusable = this._getFocusable();
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!this.dialog.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
      return;
    }

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  _getFocusable() {
    return this.dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
  }
}

if (!customElements.get('gf-quick-view')) {
  customElements.define('gf-quick-view', GfQuickView);
}

/* Global click listener for quick-view triggers */
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-quick-view-url]');
  if (!trigger) return;

  e.preventDefault();
  const url = trigger.dataset.quickViewUrl;
  const sectionId = trigger.dataset.quickViewSection || 'gf-main-product';
  const modal = document.querySelector('gf-quick-view');
  if (modal && url) modal.open(url, sectionId);
});
